// Phase 4/5 - frontend helpers that hit our Claude proxy.

const PROXY_URL = '/.netlify/functions/claude';

// ---- Phase 4: polish a mechanical legal description ----
export async function polishLegalDescription({ kind, rawText, parcel }) {
  const prompt = buildPolishPrompt({ kind, rawText, parcel });
  const resp = await fetch(PROXY_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prompt }),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(data.error || `Claude proxy error ${resp.status}`);
  if (!data.text) throw new Error('Empty response from Claude');
  return data.text.trim();
}

// ---- Phase 5+: extract a legal description from an uploaded file ----
// Accepts a File from <input type="file">. Supports PDFs and images.
// Returns the raw legal description text as Claude extracts it.
export async function extractLegalDescriptionFromFile(file) {
  if (!file) throw new Error('No file provided');
  const maxBytes = 4 * 1024 * 1024; // Netlify function bodies cap at ~6 MB; base64 adds ~33% overhead, so keep files <= 4 MB.
  if (file.size > maxBytes) throw new Error(`File too large (${(file.size/1024/1024).toFixed(1)} MB). Please keep under 4 MB — Netlify function payload limit.`);
  const mediaType = detectMediaType(file);
  if (!mediaType) throw new Error('Unsupported file type. Use PDF, PNG, JPG, GIF, or WEBP.');
  const base64 = await fileToBase64(file);

  const prompt = [
    'Extract the PROPERTY LEGAL DESCRIPTION from the attached document.',
    '',
    'Rules:',
    ' - Return ONLY the legal description text itself (what begins with',
    '   "Lot ...", "The NE 1/4 ...", "Beginning at ...", etc.).',
    ' - No preamble, no explanation, no markdown.',
    ' - Preserve standard PLSS formatting (sections, townships, ranges,',
    '   quarter-quarters, meridian, state) exactly as written.',
    ' - If the document contains multiple legal descriptions, return the',
    '   FIRST one.',
    ' - If no legal description is present in the document, respond with',
    '   the single word: NONE',
  ].join('\n');

  const resp = await fetch(PROXY_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      prompt,
      attachments: [{ media_type: mediaType, data: base64 }],
    }),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    let msg = data.error || `Claude error ${resp.status}`;
    const apiMsg = data?.detail?.error?.message;
    if (apiMsg) msg += ' - ' + apiMsg;
    throw new Error(msg);
  }
  const text = (data.text || '').trim();
  if (!text || text === 'NONE') throw new Error('No legal description detected in this document.');
  return text;
}

// ---- helpers ----
function detectMediaType(file) {
  const t = (file.type || '').toLowerCase();
  if (t === 'application/pdf') return 'application/pdf';
  if (/^image\/(png|jpe?g|gif|webp)$/.test(t)) return t === 'image/jpg' ? 'image/jpeg' : t;
  // Fallback to extension
  const name = (file.name || '').toLowerCase();
  if (name.endsWith('.pdf')) return 'application/pdf';
  if (name.endsWith('.png')) return 'image/png';
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg';
  if (name.endsWith('.gif')) return 'image/gif';
  if (name.endsWith('.webp')) return 'image/webp';
  return null;
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      // strip the data URL prefix "data:<type>;base64,"
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error('File read failed'));
    reader.readAsDataURL(file);
  });
}

function buildPolishPrompt({ kind, rawText, parcel }) {
  const props = parcel?.properties?.fields || parcel?.properties || {};
  const context = [
    props.address && `Parcel address: ${props.address}`,
    props.owner && `Owner: ${props.owner}`,
    (props.scounty || props.county) && `County: ${toTitleCase(props.scounty || props.county)}`,
    props.state2 && `State: ${props.state2}`,
    props.ll_gisacre && `Approximate acreage (Regrid): ${props.ll_gisacre}`,
    props.legaldesc && `Regrid's stored legal description (for reference only): ${props.legaldesc}`,
  ].filter(Boolean).join('\n');

  const header = [
    'You are producing a formal property legal description.',
    '',
    'CRITICAL OUTPUT RULES:',
    '- Respond with ONLY the final legal description text.',
    '- Do NOT show any analysis, reasoning, scratch work, bullet points, or',
    '  explanation of how you derived the answer.',
    '- Do NOT include preamble like "Here is the description:" or similar.',
    '- Plain text only (no markdown, no headers, no commentary).',
    '- If you need to work through the math internally, do so silently.',
  ].join('\n');

  if (kind === 'aliquot') {
    return [
      header, '',
      'FORMAT GUIDELINES for aliquot descriptions:',
      '- Combine adjacent quarter-quarters into halves, quarters, or halves of',
      '  quarters where possible.',
      '- Use abbreviations: NE 1/4, W 1/2, SE 1/4 of the NW 1/4.',
      '- One paragraph per section, in ascending numeric order.',
      '- Each paragraph MUST end with a location block in this exact order:',
      '  "Section X, Township Y North, Range Z West, <Meridian>, <County> County, <State>."',
      '- If the Context below does not list a county, append just <State>.',
      '- Capitalize county name properly (e.g. "Logan County" not "logan county").',
      '- For a section that is entirely included: "All of Section X, Township Y',
      '  North, Range Z West, 6th Principal Meridian, Logan County, Colorado."',
      '- End with a separate final line: "Containing X.XX acres, more or less."',
      '',
      'Context:',
      context || '(no additional context)', '',
      'Mechanical aliquot list to rewrite:',
      rawText,
      '',
      'FINAL REMINDER: Your reply must contain ONLY the finished legal',
      'description text. Do NOT narrate your reasoning. Do NOT write',
      '"Section X:" analysis blocks. Do NOT output any commentary. Start',
      'directly with the first paragraph of the description.',
    ].join('\n');
  }

  return [
    header, '',
    'FORMAT GUIDELINES for metes-and-bounds descriptions:',
    '- Begin with a preamble identifying the tract by address, owner, county,',
    '  and state (when available in the Context), e.g. "A tract of land situated',
    '  in <County> County, <State>, ..."',
    '- Each call on its own line beginning with "thence" (except the opening).',
    '- Preserve bearings and distances EXACTLY; do not change numeric values.',
    '- Close with "to the POINT OF BEGINNING, containing X.XX acres, more or less."',
    '',
    'Context:',
    context || '(no additional context)', '',
    'Mechanical metes-and-bounds to rewrite:',
    rawText,
    '',
    'FINAL REMINDER: Reply with ONLY the finished legal description text,',
    'starting at "A tract of land..." or "Beginning at...". No reasoning.',
  ].join('\n');
}

function toTitleCase(s) {
  return String(s).toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()).trim();
}

